import { CustomAnalyticsQueryVersionSchema } from "@redotech/redo-analytics-custom-analytics/query/custom-analytics-report-query";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";
import {
  CustomReportDraftMaxAttemptsSchema,
  CustomReportDraftOutputShape,
  CustomReportDraftTeamIdSchema,
} from "./custom-report-draft-schema";

/**
 * The merchant CustomAnalyticsReportFailureCode values this RPC can produce.
 * Drift is caught in the handler, whose return is checked against this schema.
 */
const CustomReportPromptDraftFailureCodeSchema = z.enum([
  "generation_failed",
  "query_compilation_failed",
  "query_validation_failed",
]);

export const draftCustomReportAstFromPrompt = unaryRpc({
  name: "draftCustomReportAstFromPrompt" as const,
  description:
    "Draft a custom analytics semantic query AST from a plain-English prompt by running the same agent and the same post-generation validations the merchant Ask Redo report flow runs, for the given query version. maxAttempts of 1 reproduces merchant behavior exactly, where a compile or validation failure is terminal; above 1 the failure is fed back so the agent can repair its own query, which tests whether the catalog can express the report at all.",
  input: z.object({
    prompt: z.string().trim().min(1).max(8000),
    teamId: CustomReportDraftTeamIdSchema,
    conversationContext: z.string().trim().max(20_000).nullable().default(null),
    maxAttempts: CustomReportDraftMaxAttemptsSchema.default(1),
    version: CustomAnalyticsQueryVersionSchema,
  }),
  output: z.object({
    ...CustomReportDraftOutputShape,
    failureCode: CustomReportPromptDraftFailureCodeSchema.nullable(),
    attemptsUsed: z.number().int(),
  }),
});

export type DraftCustomReportAstFromPromptInput = z.infer<
  typeof draftCustomReportAstFromPrompt.input
>;
export type DraftCustomReportAstFromPromptOutput = z.infer<
  typeof draftCustomReportAstFromPrompt.output
>;
